import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { GridFactory } from '$/factory';
import BixButton from '$/components/global/bixButton';
import { updateDraft } from '$/store/draft/actions';
import Loading from '$/components/loading/Loading';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import './CompanyAnalytics.scss';
export interface ICompanyAnalyticsProps {
  draftCompany?: Bixby.Models.DraftCompany;
  currentCompany?: Bixby.Models.AdminCompany;
  isEditing: boolean;
  isLoading: boolean;
  capitalStructure?: string[];
  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

const CompanyAnalytics: React.FC<ICompanyAnalyticsProps> = props => {
  const { draftCompany, currentCompany, isEditing, isLoading, dispatchUpdateDraftCompany } = props;

  const [incomeRows, setIncomeRows] = useState(undefined as Bixby.Models.AdminIncomeStatementStatGridRow[] | undefined);
  const [readOnlyIncomeRows, setReadOnlyIncomeRows] = useState([] as Bixby.Models.AdminIncomeStatementGridRow[]);
  const [levageStatsRows, setLeverageStatsRows] = useState([] as Bixby.Models.AdminLeverageStatsGridRow[]);
  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    if (isEditing && draftCompany) {
      setIncomeRows(
        GridFactory.buildClientCapitalStructure(
          draftCompany.analyticsLastTwelveMonths,
          draftCompany.analyticsPriorQuarter,
          draftCompany.analyticsCurrentQuarter,
          draftCompany.loanDetails.creditors
        )
      );
      setReadOnlyIncomeRows(
        GridFactory.buildAdminCapitalStructureTable(
          draftCompany.analyticsLastTwelveMonths,
          draftCompany.analyticsPriorQuarter,
          draftCompany.analyticsCurrentQuarter
        )
      );
      setLeverageStatsRows(GridFactory.buildAdminLeverageStatsTable(draftCompany.analyticsLastTwelveMonths));
    } else if (!isEditing && currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
      setIncomeRows(
        GridFactory.buildClientCapitalStructure(
          currentCompany.analyticsLastTwelveMonths,
          currentCompany.analyticsPriorQuarter,
          currentCompany.analyticsCurrentQuarter,
          currentCompany.loanDetails.creditors
        )
      );
      setReadOnlyIncomeRows(
        GridFactory.buildAdminCapitalStructureTable(
          currentCompany.analyticsLastTwelveMonths,
          currentCompany.analyticsPriorQuarter,
          currentCompany.analyticsCurrentQuarter
        )
      );
      setLeverageStatsRows(GridFactory.buildAdminLeverageStatsTable(currentCompany.analyticsLastTwelveMonths));
    }
  }, [draftCompany, currentCompany, isEditing]);

  useEffect(() => {
    if (incomeRows && incomeRows.length) {
      const analytics = GridFactory.convertTableToCompanyAnalytics(incomeRows);
      if (
        analytics &&
        analytics.analyticsCurrentQuarter &&
        analytics.analyticsLastTwelveMonths &&
        analytics.analyticsPriorQuarter
      ) {
        setReadOnlyIncomeRows(
          GridFactory.buildAdminCapitalStructureTable(
            analytics.analyticsLastTwelveMonths,
            analytics.analyticsPriorQuarter,
            analytics.analyticsCurrentQuarter
          )
        );
        setLeverageStatsRows(GridFactory.buildAdminLeverageStatsTable(analytics.analyticsLastTwelveMonths));
      }
    }
  }, [incomeRows]);

  const handleSaveDraft = () => {
    if (draftCompany && incomeRows) {
      const analytics = GridFactory.convertTableToCompanyAnalytics(incomeRows);
      const updatedValues = [
        {
          op: 'replace',
          path: `/analyticsLastTwelveMonths`,
          value: analytics.analyticsLastTwelveMonths,
          from: '',
        },
        {
          op: 'replace',
          path: `/analyticsPriorQuarter`,
          value: analytics.analyticsPriorQuarter,
          from: '',
        },
        {
          op: 'replace',
          path: `/analyticsCurrentQuarter`,
          value: analytics.analyticsCurrentQuarter,
          from: '',
        },
      ] as Bixby.Models.JsonPatchOperation[];
      dispatchUpdateDraftCompany(draftCompany.id, updatedValues);
    }
  };

  const incomeStatementStatRows = readOnlyIncomeRows.map((row, index) => {
    return (
      <tr key={index}>
        <th>{row.rowTitle}</th>
        <td>{row.ltm as string}</td>
        <td>{row.priorQuarter as string}</td>
        <td>{row.currentQuarter as string}</td>
      </tr>
    );
  });

  const leverageStatRows = levageStatsRows.map((row, index) => {
    return (
      <tr key={index}>
        <th>{row.rowTitle}</th>
        <td>{row.value as string}</td>
        <td>{row.ebitda}</td>
        <td>{row.adjustedEbitda}</td>
        <td>{row.covenantEbitda}</td>
      </tr>
    );
  });

  const handleCellValueChange = () => {
    const rowData: Bixby.Models.AdminIncomeStatementStatGridRow[] = [];
    if (gridApi) {
      gridApi.forEachNode(node => rowData.push(node.data));
    }
    setIncomeRows(rowData);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  return isLoading || !incomeRows ? (
    <Loading inline />
  ) : (
    <React.Fragment>
      <section>
        <div className="admin-profile-section">
          <h3>Data Inputs</h3>
          {isEditing && (
            <div>
              <div className="company-analytics-data-grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      suppressContextMenu={true}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        sortable: false,
                        resizable: false,
                        lockPinned: true,
                        cellClass: 'align-left',
                      }}
                      onGridReady={onGridReady}
                      rowData={incomeRows}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      enableRangeSelection={true}
                      animateRows={true}
                      rowHeight={32}
                      headerHeight={32}
                      onCellValueChanged={handleCellValueChange}
                      suppressLastEmptyLineOnPaste={true}
                      columnDefs={[
                        { headerName: '', field: 'rowTitle' },
                        { headerName: 'Line Item', field: 'lineItem' },
                        { headerName: 'LTM', field: 'ltm', editable: true },
                        { headerName: 'Prior Quarter', field: 'priorQuarter', editable: true },
                        { headerName: 'Current Quarter', field: 'currentQuarter', editable: true },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>

              <BixButton type="button" disabled={!Bixby.Utils.isDefined(draftCompany)} onClick={handleSaveDraft}>
                Save as Draft
              </BixButton>
            </div>
          )}
          {!isEditing && (
            <div className="company-analytics-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    suppressContextMenu={true}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: false,
                      resizable: false,
                      lockPinned: true,
                      cellClass: 'align-left',
                    }}
                    onGridReady={onGridReady}
                    rowData={incomeRows}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    enableRangeSelection={false}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    onCellValueChanged={handleCellValueChange}
                    suppressLastEmptyLineOnPaste={true}
                    columnDefs={[
                      { headerName: '', field: 'rowTitle' },
                      { headerName: 'Line Item', field: 'lineItem' },
                      { headerName: 'LTM', field: 'ltm' },
                      { headerName: 'Prior Quarter', field: 'priorQuarter' },
                      { headerName: 'Current Quarter', field: 'currentQuarter' },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <section>
        <div className="admin-profile-section bottom">
          <h3>Financial Output</h3>
          <table className="table admin_capital_structure_table">
            <thead>
              <tr>
                <th />
                <th>LTM</th>
                <th>Prior Quarter</th>
                <th>Current Quarter</th>
              </tr>
            </thead>
            <tbody>{incomeStatementStatRows}</tbody>
          </table>
          <h3>Capital Structure Output</h3>
          <table className="table admin_leverage_stats_table">
            <thead>
              <tr>
                <th />
                <th />
                <th>EBITDA</th>
                <th>Adj. EBITDA</th>
                <th>Covenant EBITDA</th>
              </tr>
            </thead>
            <tbody>{leverageStatRows}</tbody>
          </table>
        </div>
      </section>
    </React.Fragment>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    isEditing: state.draftState.isEditing,
    draftCompany: state.draftState.draftCompany,
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAnalytics);
